import { faGears } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VariantProps, cva } from 'class-variance-authority';
import { memo, useContext, useEffect, useState } from 'react';
import { AppContext } from '~/App';
import { initials } from '~/helpers/initials';

const avatarStyles = cva(
  'aspect-square flex shrink-0 items-center justify-center overflow-hidden text-center font-medium no-underline text-secondary',
  {
    variants: {
      size: {
        small: 'h-6 w-6 text-[11px]',
        medium: 'h-6 w-6 lg:h-8 lg:w-8',
        large: 'h-7 w-7 lg:h-9 lg:w-9',
        xl: 'h-10 w-10 lg:h-12 lg:w-12',
        full: '',
        'full-small-text': 'text-[12px]',
      },
      square: {
        // This should be changed in a further iteration to move text settings away from "square" option as that is not clear.
        true: 'bg-shade-10 text-shade-50 text-lg lg:text-xl',
        false: 'rounded-full',
      },
      bg: {
        white: 'bg-white',
        grey: 'bg-grey-20',
      },
    },
    defaultVariants: {
      size: 'medium',
      square: false,
      bg: 'grey',
    },
    // This should also be changed in a further iteration to move text settings away from "square" option as that is not clear.
    compoundVariants: [
      {
        size: ['full', 'medium', 'large'],
        square: false,
        className: 'text-xs lg:text-sm',
      },
    ],
  }
);

type Props = VariantProps<typeof avatarStyles> & {
  /**
   * A name to be turned into initials
   */
  name?: string;
  /**
   * URL to a profile picture
   */
  image?: string | null | undefined;
  automation?: boolean;
  className?: string;
};

export const CurrentAvatar = (props: Props) => {
  const { user: currentUser } = useContext(AppContext);
  return (
    <Avatar {...props} name={currentUser?.name} image={currentUser?.image} />
  );
};

/**
 * Display an image or initials representing a person
 */
export const Avatar = memo(
  ({ name, image, className, automation = false, square, size, bg }: Props) => {
    const [imageError, setImageError] = useState(false);

    // Reset error state when image URL changes
    useEffect(() => {
      if (image) {
        setImageError(false);
      }
    }, [image]);

    const avatarImage = (() => {
      if (automation) {
        return (
          <FontAwesomeIcon
            icon={faGears}
            className='h-full w-full p-1.5 text-primary'
          />
        );
      } else if (image && !imageError) {
        return (
          <img
            src={image}
            alt={name || 'Avatar'}
            className='h-full w-full object-cover'
            onError={() => setImageError(true)}
            style={{ display: 'block' }}
          />
        );
      } else if (name) {
        return initials(name).toUpperCase();
      } else {
        return 'U';
      }
    })();

    return (
      <abbr
        className={avatarStyles({ square, size, className, bg })}
        style={{ textDecoration: 'none' }}
        title={name ?? 'Unassigned'}
      >
        {avatarImage}
      </abbr>
    );
  }
);

Avatar.displayName = 'Avatar';
